import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { AddOnVisibilityOperationType } from '../../common/enums.js';
import { useChangeOffer } from '../../common/hooks/useChangeOffer';
import { changeFeeMsg, orderMsg, phoneNumberMsg, t, userMsg } from '../../common/i18n';
import {
  filterAddonsFromRingSubs,
  filterChangeOfferAddonsFromRingSubs,
  filterVisibilitiesFromRingSubs,
  isRingSubscription,
} from '../../common/utils/ringUtils.js';
import { SalesType } from '../../generated/api/salesType.js';
import { ButtonGroupForSubmitAndBack } from '../ButtonGroupForSubmitAndBack/ButtonGroupForSubmitAndBack.js';
import { AdditionalServicesCheckoutContent } from '../OrderSubscriptionConfiguration/AdditionalServicesCheckoutContent.js';
import { OrderSummary, OrderSummaryType } from '../OrderSummary/OrderSummary.js';
import { getNonEditableAddOns } from '../SubscriptionAdditionalService/subscriptionAddOnUtils';
import { SUBSCRIPTION_CHANGE_SERVICE_FEE } from './content/LaitenettiSubscriptionCardContent';
import {
  filterAddOns,
  formatPrice,
  getAddOnFormData,
  getAddOnPayload,
  getAddedAddOnForOpeningFee,
  getIconType,
  getMonthlyPrice,
  getPreselectedAddOns,
  handleChangeOffer,
  isVakioSubscription,
  removeDuplicateAddedAddOns,
  shouldUpdateState,
} from './subscriptionChangeConfirmationUtils.js';
import type { AddedAddon } from '../../common/types/addon.js';
import type { ConfiguredCommercialProduct } from '../../common/types/commercialProduct.js';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse';
import type { AddOnVisibility } from '../../generated/api/addOnVisibility.js';
import type { CampaignAssociation } from '../../generated/api/campaignAssociation.js';
import type { CommercialProduct } from '../../generated/api/commercialProduct.js';
import type { Offer } from '../../generated/api/offer.js';
import type { Subscription } from '../../generated/api/subscription.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { SubscriptionAddOnAttributesMap } from '../../selfservice/actions';
import type { AddOnSelectionType, OrderConfiguration } from '../OrderSubscriptionConfiguration/FormWrapper.js';

import './SubscriptionChangeConfirmation.scss';

const FORM_CONFIG_NAME = 'changeOfferForm';

interface SubscriptionChangeConfirmationProps {
  selectedCommercialProduct: CommercialProduct;
  subscription: Subscription;
  addOnRules: AddOnRulesResponse;
  addOnRulesMobilePbx: AddOnRulesResponse;
  addOnVisibilities: AddOnVisibility[];
  selectedAddOns?: AddedAddon[];
  campaignAssociation?: CampaignAssociation;
  campaignDescription?: string;
  pbxSolutions: Subscription[];
  selectedServiceFee?: { price: number; productId: string };
  onlineModelName: string;
  offer: Offer;
  previousOffer?: Offer;
  voucher?: string;
}

const ContentHeader = ({
  commercialProduct,
  campaignAssociation,
  selectedServiceFee = SUBSCRIPTION_CHANGE_SERVICE_FEE,
}: {
  commercialProduct: CommercialProduct;
  campaignAssociation?: CampaignAssociation;
  selectedServiceFee?: number;
}) => {
  const iconType = getIconType(commercialProduct.productSubType);

  return (
    <div className="of-subscription-change-confirmation__header">
      <div className={`of-subscription-change-confirmation__header__icon__${iconType}`}></div>
      <div className="of-subscription-change-confirmation__header__content">
        <div className="of-subscription-change-confirmation__header__content__wrapper">
          <div className="of-subscription-change-confirmation__header__content__wrapper__product-name">
            {commercialProduct.commercialProductName}
          </div>
          <div className="of-subscription-change-confirmation__header__content__wrapper__product-price">
            {formatPrice(getMonthlyPrice(commercialProduct, campaignAssociation))} {t.H37G('€/month')}
          </div>
        </div>
        <div className={dsClass.MARGIN_TOP_4}>
          {t.ZRKX(changeFeeMsg)} {formatPrice(selectedServiceFee)} €
        </div>
      </div>
    </div>
  );
};

const PhoneNumberAndUserDetails = ({ subscription }: { subscription: Subscription }) => (
  <>
    <div className="of-subscription-change-confirmation__details__content__header">{t.AGIJ(phoneNumberMsg)}</div>
    <div className="of-subscription-change-confirmation__details__content__value">
      {subscription.subscriptionUserFriendlyId}
    </div>
    <div className="of-subscription-change-confirmation__details__content__header">{t.LUZM(userMsg)}</div>
    <div>{subscription.subscriptionPurposeOfUse}</div>
  </>
);

export const SubscriptionChangeConfirmation = ({
  selectedCommercialProduct,
  subscription,
  addOnRules,
  addOnRulesMobilePbx,
  selectedAddOns = [],
  campaignAssociation,
  addOnVisibilities,
  campaignDescription,
  pbxSolutions,
  selectedServiceFee,
  onlineModelName,
  offer,
  previousOffer,
  voucher,
}: SubscriptionChangeConfirmationProps) => {
  const postChangeOffer = useChangeOffer();
  const isRingSub = isRingSubscription(subscription, pbxSolutions);
  const isVakioSub = isVakioSubscription(pbxSolutions, subscription);
  const visibilitiesConsideringRing = isRingSub ? filterVisibilitiesFromRingSubs(addOnVisibilities) : addOnVisibilities;
  const handleChangeOfferResult = handleChangeOffer(
    subscription,
    selectedCommercialProduct,
    addOnRules,
    addOnRulesMobilePbx,
    isRingSub,
    addOnVisibilities
  );
  const { addOnsToAdd, addOnsToUpdate, addOnsToRemove } = filterChangeOfferAddonsFromRingSubs({
    ...handleChangeOfferResult,
    isRingSub,
  });
  // This is needed in order to show the order summary correctly in the first render
  const preSelectedAddOns = getPreselectedAddOns(
    addOnsToAdd,
    addOnsToUpdate,
    isRingSub ? filterAddonsFromRingSubs(selectedAddOns) : selectedAddOns,
    visibilitiesConsideringRing
  );

  // All addons that are included in the new subscription
  const subscriptionAddOns = Array.from(new Set([...addOnsToAdd, ...addOnsToUpdate]));

  // Addons that cannot be edited but are included in the subscription (e.g. Reissunetti in voice Perus)
  const nonEditableAddons: AddedAddon[] = getNonEditableAddOns(visibilitiesConsideringRing, subscriptionAddOns);

  const [formAddOns, setFormAddOns] = useState<AddOnSelectionType | undefined>({ addedAddOns: selectedAddOns });
  const [configuredCommercialProduct, setConfiguredCommercialProduct] = useState<ConfiguredCommercialProduct>({
    commercialProduct: selectedCommercialProduct,
    addedAddOns: preSelectedAddOns,
    selectedCampaignAssociation: campaignAssociation,
    purposeOfUseOrContact: {
      contactName: subscription.subscriptionContactName,
    },
  });
  const editableAddons: AddedAddon[] = preSelectedAddOns.filter(
    preSel => !nonEditableAddons.some(nonEdit => nonEdit.addOnCode === preSel.addOnCode)
  );
  const navigate = useNavigate();
  const methods = useForm<OrderConfiguration>({
    mode: 'all',
    // needed since editable addons are updated in useEffect on first-render, so that order summary need to be updated with the correct values
    defaultValues: { configuration: { [FORM_CONFIG_NAME]: { addOns: { addedAddOns: editableAddons } } } },
  });
  const formValues = methods.watch();

  const onSubmit = () => {
    const { codesToAdd, codesToUpdate, codesToRemove } = getAddOnPayload(
      addOnsToAdd,
      addOnsToUpdate,
      addOnsToRemove,
      isRingSub,
      formAddOns
    );
    const addOnAttributesMap = configuredCommercialProduct.addedAddOns?.reduce((acc, addOn) => {
      if (addOn.addOnAttributes) {
        acc[addOn.addOnCode] = addOn.addOnAttributes;
      }
      return acc;
    }, {} as SubscriptionAddOnAttributesMap);

    void postChangeOffer(
      subscription,
      selectedCommercialProduct.commercialProductCode,
      codesToAdd,
      codesToRemove,
      codesToUpdate,
      campaignAssociation,
      addOnAttributesMap,
      selectedServiceFee?.productId,
      onlineModelName,
      offer,
      previousOffer,
      voucher
    );
  };

  // Keep order summary in sync with form values. Get values from the form and compare the values to the current
  // values in component state. If there has been changes, set new values to the state, so that order summary can be
  // updated. Payload check is needed in order to avoid endless loop and unwanted initial render with empty payload.
  useEffect(() => {
    const updatedFormAddOns: AddOnSelectionType | undefined = getAddOnFormData(formValues as OrderConfiguration);

    if (shouldUpdateState(updatedFormAddOns, formAddOns)) {
      setFormAddOns({ ...updatedFormAddOns });
      setConfiguredCommercialProduct({
        ...configuredCommercialProduct,
        selectedCampaignAssociation: campaignAssociation,
        addedAddOns: [...nonEditableAddons, ...removeDuplicateAddedAddOns(updatedFormAddOns?.addedAddOns || [])],
      });
    }
  }, [
    formValues,
    formAddOns,
    setConfiguredCommercialProduct,
    configuredCommercialProduct,
    selectedCommercialProduct,
    subscription,
    nonEditableAddons,
    campaignAssociation,
  ]);

  return (
    <div className="of-subscription-change-confirmation">
      <div>
        <h3>{t.OKGT('Subscription details')}</h3>
      </div>
      <hr />
      <ContentHeader
        commercialProduct={selectedCommercialProduct}
        campaignAssociation={campaignAssociation}
        selectedServiceFee={selectedServiceFee?.price}
      />
      <div className="of-subscription-change-confirmation__details__wrapper">
        <div className="of-subscription-change-confirmation__details">
          <div className="of-subscription-change-confirmation__details__content">
            <PhoneNumberAndUserDetails subscription={subscription} />
            <FormProvider {...methods}>
              <form noValidate>
                <div>
                  <AdditionalServicesCheckoutContent
                    addOnAssociations={(addOnRules.associations?.[0] as AssociationRecord[]) || []}
                    addOnDependencies={(addOnRules.dependencies?.[0] as DependencyRecord[]) || []}
                    commercialProduct={selectedCommercialProduct}
                    isRingSelected={isRingSub}
                    cartItemInstanceId={FORM_CONFIG_NAME}
                    productAddOns={Array.from(new Set([...selectedAddOns, ...subscriptionAddOns]))}
                    preFetchedAddOnVisibilities={addOnVisibilities}
                    addOnVisibilityOperationType={AddOnVisibilityOperationType.CHANGE}
                    isVakioSubscription={isVakioSub}
                  />
                </div>
              </form>
            </FormProvider>
          </div>
        </div>

        <div className="of-subscription-change-confirmation__details__order-summary">
          <OrderSummary
            commercialProducts={[
              {
                ...configuredCommercialProduct,
                addedAddOns: filterAddOns(
                  configuredCommercialProduct.commercialProduct.commercialProductName,
                  [
                    ...(configuredCommercialProduct.addedAddOns || []),
                    getAddedAddOnForOpeningFee(selectedServiceFee?.price),
                  ],
                  isRingSub
                ),
              },
            ]}
            summaryType={OrderSummaryType.DETAILS_CLOSED}
            campaignAssociation={campaignAssociation}
            campaignDescription={campaignDescription}
            salesType={SalesType.CHANGE_OFFER}
          />
        </div>
        <div className="of-subscription-change-confirmation__details__buttons">
          <ButtonGroupForSubmitAndBack
            submitButtonText={t.AQOL(orderMsg)}
            onSubmit={() => methods.handleSubmit(onSubmit)()}
            onCancel={() => navigate(-1)}
          />
        </div>
      </div>
    </div>
  );
};
